import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { Printer, Download } from "lucide-react";
import { api, API } from "@/lib/api";
import { useAdmin } from "@/context/AdminContext";
import { ClaimStatusBadge, CiteThis, RevisionMeta, CitationList, PrimaryDocuments } from "@/components/shared";

export const ClaimDetailPage = () => {
  const { claimId } = useParams();
  const [claim, setClaim] = useState(null);
  const [citations, setCitations] = useState([]);
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    api.get(`/claims/${claimId}`).then(res => {
      setClaim(res.data.claim);
      setCitations(res.data.citations);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [claimId]);
  
  if (loading) {
    return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>;
  }
  
  if (!claim) {
    return <div className="max-w-4xl mx-auto px-4 py-12"><p>Claim not found</p></div>;
  }
  
  const pageUrl = typeof window !== 'undefined' ? window.location.href : '';
  
  return (
    <div className="max-w-4xl mx-auto px-4 py-12" data-testid="claim-detail-page">
      <div className="flex items-center justify-end mb-4 gap-2">
        <a 
          href={`${API}/pdf/claims/${claimId}`}
          className="btn-secondary inline-flex items-center gap-2 text-sm"
          data-testid="export-pdf-btn"
          download
        >
          <Download className="w-4 h-4" />
          Export PDF
        </a>
        <Link 
          to={`/print/claim/${claimId}`}
          className="btn-secondary inline-flex items-center gap-2 text-sm"
          data-testid="print-claim-btn"
        >
          <Printer className="w-4 h-4" />
          Print
        </Link>
      </div>
      
      <article className="archive-card p-8">
        <header className="mb-6 pb-6 border-b">
          <div className="flex items-start justify-between gap-4 mb-4">
            <h1 className="text-2xl font-bold serif-heading">{claim.claim_text}</h1>
            <ClaimStatusBadge status={claim.claim_status} />
          </div>
          {claim.summary && (
            <p className="text-gray-600">{claim.summary}</p>
          )}
        </header>
        
        {claim.detail && (
          <section className="mb-6">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-2">
              Detail
            </h2>
            <p className="text-gray-700 leading-relaxed">{claim.detail}</p>
          </section>
        )}
        
        {citations.length > 0 && (
          <section className="mb-6">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-gray-500 mb-3">
              Citations ({citations.length})
            </h2>
            <CitationList citations={citations} />
          </section>
        )}
        
        {/* Primary Documents */}
        <PrimaryDocuments 
          entityType="claims" 
          entityId={claim.id}
          title="Supporting Documents"
        />
        
        <RevisionMeta updatedAt={claim.updated_at} />
        
        <CiteThis 
          title={claim.claim_text}
          date={claim.updated_at}
          url={pageUrl}
        />
      </article>
    </div>
  );
};

export default ClaimDetailPage;
